import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useGlobalFilters } from '../../hooks/useGlobalFilters';
import { allCommunitiesAverageStore } from './AllCommunitiesAverageStore';
import { predictionTrendsStore } from "../../Analysis_A/PredictionTrendsStore";
import DataWindowSlider from '../../components/DataWindowSliderPV';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import './ActivityAnalysis.scss';
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

// Регистрация компонентов Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function formatNumber(val) {
  if (typeof val === "number") {
    return val.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  }
  if (!isNaN(Number(val))) {
    return Number(val).toLocaleString("ru-RU", { maximumFractionDigits: 2 });
  }
  return val ?? "";
}

function formatDate(date) {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("ru-RU");
}

function getGroupNamesByIds(ids) {
  if (!Array.isArray(ids)) return [];
  return ids.map(id => {
    const group = predictionTrendsStore.groups.find(g => String(g.id) === String(id));
    return group ? group.name : id;
  });
}

const AllCommunitiesAverageChart = observer(() => {
  const globalFilters = useGlobalFilters();
  const store = allCommunitiesAverageStore;
  const [dataWindow, setDataWindow] = React.useState({ start: 0, end: 100 });
  const [aggregation, setAggregation] = React.useState('day'); // 'day' | 'week'

  // Функция для обновления окна данных
  const updateWindow = (value) => {
    const newStart = Number(value);
    setDataWindow({ start: newStart, end: newStart + 100 });
  };

  // Функция агрегации данных
  const aggregateData = (data, period) => {
    if (!data || data.length === 0) return [];

    const grouped = {};
    
    data.forEach(item => {
      const date = new Date(item.time);
      let key;
      
      switch (period) {
        case 'week':
          // Начало недели (понедельник)
          const dayOfWeek = date.getDay();
          const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
          const monday = new Date(date.setDate(diff));
          key = monday.toISOString().split('T')[0];
          break;
        default: // 'day'
          key = date.toISOString().split('T')[0];
          break;
      }
      
      if (!grouped[key]) {
        grouped[key] = {
          time: key,
          activities: [],
          avg_activities: []
        };
      }
      
      grouped[key].activities.push(item.activity || 0);
      grouped[key].avg_activities.push(item.avg_activity || 0);
    });

    return Object.values(grouped)
      .map(group => {
        const avgActivity = group.activities.reduce((sum, val) => sum + val, 0) / group.activities.length;
        const avgOfAvg = group.avg_activities.reduce((sum, val) => sum + val, 0) / group.avg_activities.length;
        
        return {
          time: group.time,
          activity: avgActivity,
          avg_activity: avgOfAvg
        };
      })
      .sort((a, b) => new Date(a.time) - new Date(b.time));
  };

  // Автоматически обновляем фильтры и подгружаем данные при изменении глобальных фильтров
  React.useEffect(() => {
    store.setFilters({
      startDate: globalFilters.startDate,
      endDate: globalFilters.endDate,
      selectedGroupIds: globalFilters.selectedGroups,
      aggregation: aggregation
    });
    store.fetchData();
    // eslint-disable-next-line
  }, [globalFilters.filtersString, aggregation]);

  // Получаем данные по сообществам
  const dataByCommunity = store.dataByCommunity;

  // Получаем названия сообществ
  const communityNames = useMemo(() => {
    const names = {};
    Object.keys(dataByCommunity).forEach(communityId => {
      const group = predictionTrendsStore.groups.find(g => String(g.id) === String(communityId));
      names[communityId] = group ? group.name : `Сообщество ${communityId}`;
    });
    return names;
  }, [dataByCommunity]);

  // Данные для графика средних линий всех сообществ
  const allCommunitiesChartData = useMemo(() => {
    if (!dataByCommunity || Object.keys(dataByCommunity).length === 0) return null;

    // Получаем все уникальные даты (данные уже агрегированы на сервере)
    const allDates = store.allDates;

    // Применяем окно данных
    const startIndex = Math.min(dataWindow.start, allDates.length - 1);
    const endIndex = Math.min(dataWindow.end, allDates.length);
    const visibleDates = allDates.slice(startIndex, endIndex);

    // Создаем датасеты для каждого сообщества
    const datasets = [];
    const colors = [
      '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
      '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
    ];

    Object.keys(dataByCommunity).forEach((communityId, index) => {
      const communityData = dataByCommunity[communityId];
      
      // Создаем мапу для быстрого поиска данных по дате
      const dataMap = {};
      communityData.forEach(item => {
        dataMap[item.time] = item.avg_activity;
      });

      // Создаем данные для видимых дат
      const communityLineData = visibleDates.map(date => {
        return dataMap[date] || null; // null для пропуска точек, где нет данных
      });

      datasets.push({
        label: communityNames[communityId],
        data: communityLineData,
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length],
        fill: false,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointBackgroundColor: colors[index % colors.length],
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        borderWidth: 3,
        spanGaps: true, // Соединяем точки, пропуская пропущенные данные
      });
    });

    // Добавляем общую среднюю линию
    if (datasets.length > 0) {
      const overallAverageData = visibleDates.map(date => {
        let sum = 0;
        let count = 0;
        
        Object.keys(dataByCommunity).forEach(communityId => {
          const communityData = dataByCommunity[communityId];
          const dataMap = {};
          communityData.forEach(item => {
            dataMap[item.time] = item.avg_activity;
          });
          
          if (dataMap[date] !== undefined) {
            sum += dataMap[date];
            count++;
          }
        });
        
        return count > 0 ? sum / count : null;
      });

      datasets.push({
        label: 'Общая средняя',
        data: overallAverageData,
        borderColor: '#1f2937',
        backgroundColor: '#1f2937',
        fill: false,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 8,
        pointBackgroundColor: '#1f2937',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 3,
        borderWidth: 4,
        borderDash: [5, 5], // Пунктирная линия для общей средней
        spanGaps: true,
      });
    }

    return {
      labels: visibleDates,
      datasets: datasets
    };
  }, [dataByCommunity, dataWindow, communityNames, store.allDates]);

  // Максимальная длина данных для слайдера
  const maxDataLength = useMemo(() => {
    return store.allDates.length;
  }, [store.allDates]);

  // Текст диапазона дат для текущего окна данных
  const dateRangeText = useMemo(() => {
    if (maxDataLength === 0) {
      return 'Нет данных';
    }
    
    const allDates = store.allDates;
    const startIndex = Math.min(dataWindow.start, allDates.length - 1);
    const endIndex = Math.min(dataWindow.end - 1, allDates.length - 1);
    
    const startDate = new Date(allDates[startIndex]);
    const endDate = new Date(allDates[endIndex]);
    
    const formatDate = (date) => {
      return !isNaN(date) ? date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }) : 'Недоступно';
    };
    
    return `Показаны данные: ${formatDate(startDate)} - ${formatDate(endDate)}`;
  }, [dataWindow, maxDataLength, store.allDates]);

  // Сброс окна данных при получении новых данных или изменении агрегации
  React.useEffect(() => {
    setDataWindow({ start: 0, end: 100 });
  }, [maxDataLength, aggregation]);

  // Настройки графика
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 2.5,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#2d3748',
          font: { size: 14, family: "'Inter', sans-serif" },
          usePointStyle: true,
          pointStyle: 'line',
          padding: 20,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: (context) => {
            const datasetLabel = context.dataset.label || '';
            const value = context.parsed.y;
            if (value === null || value === undefined) return `${datasetLabel}: Нет данных`;
            return `${datasetLabel}: ${value.toFixed(2)}`;
          },
          title: (tooltipItems) => {
            const label = tooltipItems[0].label;
            const date = new Date(label);
            return isNaN(date.getTime()) ? 'Недоступно' : date.toLocaleDateString('ru-RU');
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Время',
          color: '#2d3748',
          font: { size: 14, family: "'Inter', sans-serif", weight: '600' },
        },
        grid: {
          display: false, // Убираем сетку
        },
        border: {
          display: false, // Убираем границы
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 20,
          maxRotation: 45,
          minRotation: 45,
          color: '#4a5568',
          font: { size: 12, family: "'Inter', sans-serif" },
          callback: function (value) {
            const label = this.getLabelForValue(value);
            if (!label || typeof label !== 'string') {
              return 'Недоступно';
            }
            const date = new Date(label);
            if (isNaN(date.getTime())) {
              return 'Недоступно';
            }
            return date.toLocaleDateString('ru-RU');
          },
        },
      },
      y: {
        title: {
          display: true,
          text: 'Средняя активность',
          color: '#2d3748',
          font: { size: 14, family: "'Inter', sans-serif", weight: '600' },
        },
        beginAtZero: true,
        grid: {
          display: false, // Убираем сетку
        },
        border: {
          display: false, // Убираем границы
        },
        ticks: {
          color: '#4a5568',
          font: { size: 12, family: "'Inter', sans-serif" },
        },
      },
    },
    devicePixelRatio: 2,
    elements: {
      line: {
        borderWidth: 3,
        tension: 0.4,
      },
      point: {
        radius: 4,
        hoverRadius: 6,
        borderWidth: 2,
      },
    },
  };

  // Экспорт в Excel
  const handleExportExcel = async () => {
    const filters = globalFilters.filtersString ? JSON.parse(globalFilters.filtersString) : {};
    const startDate = filters.startDate || "";
    const endDate = filters.endDate || "";
    const groupNames = getGroupNamesByIds(filters.selectedGroups || []);
    const graphTitle = "Средние_линии_всех_сообществ";
    const fileName = `${graphTitle}_${groupNames.join("_")}_${startDate}_${endDate}.xlsx`;
    
    const ws_data = [["Date", "Сообщество", "Средняя активность"]];
    
    if (allCommunitiesChartData) {
      allCommunitiesChartData.datasets.forEach(dataset => {
        if (dataset.label !== 'Общая средняя') {
          dataset.data.forEach((value, index) => {
            if (value !== null) {
              ws_data.push([
                formatDate(allCommunitiesChartData.labels[index]),
                dataset.label,
                formatNumber(value)
              ]);
            }
          });
        }
      });
    }
    
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Данные");
    XLSX.writeFile(wb, fileName);
  };

  // Экспорт в PDF
  const handleExportPDF = async () => {
    const filters = globalFilters.filtersString ? JSON.parse(globalFilters.filtersString) : {};
    const startDate = filters.startDate || "";
    const endDate = filters.endDate || "";
    const groupNames = getGroupNamesByIds(filters.selectedGroups || []);
    const graphTitle = "Средние_линии_всех_сообществ";
    const fileName = `${graphTitle}_${groupNames.join("_")}_${startDate}_${endDate}.pdf`;
    
    const input = document.querySelector("#all-communities-average-chart-container");
    if (!input) { alert("График не найден для экспорта!"); return; }
    const canvas = await html2canvas(input);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");
    pdf.addImage(imgData, "PNG", 10, 10, 270, 80);
    
    // Добавляем таблицу с данными
    const tableData = [];
    if (allCommunitiesChartData) {
      allCommunitiesChartData.datasets.forEach(dataset => {
        if (dataset.label !== 'Общая средняя') {
          dataset.data.forEach((value, index) => {
            if (value !== null) {
              tableData.push([
                formatDate(allCommunitiesChartData.labels[index]),
                dataset.label,
                formatNumber(value)
              ]);
            }
          });
        }
      });
    }
    
    autoTable(pdf, {
      startY: 100,
      head: [["Date", "Сообщество", "Средняя активность"]],
      body: tableData,
      styles: { font: "helvetica", fontSize: 10 }
    });
    pdf.save(fileName);
  };

  // Рендеринг компонента
  return (
    <div className="activity-analysis">
      <div className="activity-analysis-container">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16, width: '100%', flex: '1 1 100%' }}>
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Селектор агрегации */}
            <select 
              value={aggregation} 
              onChange={e => setAggregation(e.target.value)} 
              style={{ 
                fontSize: 16, 
                padding: '6px 12px', 
                borderRadius: 8, 
                border: '1px solid #1976d2', 
                color: '#1976d2', 
                fontWeight: 500,
                backgroundColor: '#fff'
              }}
            >
              <option value="day">По дням</option>
              <option value="week">По неделям</option>
            </select>
          </div>
          
          {/* Контейнер графика */}
          <div className="activity-analysis-content-box" style={{
            width: '100%'
          }}>
            {/* Слайдер прокрутки данных */}
            {maxDataLength > 100 && !store.loading && !store.error && allCommunitiesChartData && (
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
                <DataWindowSlider
                  dataWindow={dataWindow}
                  maxDataLength={maxDataLength}
                  updateWindow={updateWindow}
                  dateRangeText={dateRangeText}
                />
              </div>
            )}

            {/* График */}
            <div className="activity-analysis-chart-wrapper" id="all-communities-average-chart-container" style={{
              height: '70vh',
              width: '100%',
              borderRadius: 12,
              padding: 0,
              position: 'relative'
            }}>
              {store.loading ? (
                <div className="activity-analysis-no-data">Загрузка...</div>
              ) : store.error ? (
                <div className="activity-analysis-no-data">Ошибка: {store.error.message}</div>
              ) : allCommunitiesChartData ? (
                <div style={{ height: '100%', width: '100%' }}>
                  <Line data={allCommunitiesChartData} options={chartOptions} />
                </div>
              ) : globalFilters.selectedGroups.length ? (
                <div className="activity-analysis-no-data">К сожалению на данный момент для выбранных групп нет данных, попробуйте выбрать другие сообщества</div>
              ) : (
                <div className="activity-analysis-no-data">Выберите сообщества в верхней панели для отображения графика</div>
              )}
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24, marginRight: 24 }}>
              <button className="activity-analysis-export-btn" onClick={handleExportExcel}>Экспорт в Excel</button>
              <button className="activity-analysis-export-btn" onClick={handleExportPDF}>Экспорт в PDF</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default AllCommunitiesAverageChart;
